import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import DateInput from '../components/DateInput';

// (임시 CSS)
const styles = {
  pageContainer: { maxWidth: '800px', margin: '0 auto' },
  tabs: { display: 'flex', borderBottom: '1px solid #ccc' },
  tabButton: { padding: '10px 15px', cursor: 'pointer', border: 'none', background: '#f0f0f0', borderBottom: '1px solid #ccc' },
  activeTab: { background: '#fff', border: '1px solid #ccc', borderBottom: '1px solid #fff', fontWeight: 'bold' },
  tabContent: { padding: '20px', border: '1px solid #ccc', borderTop: 'none' },
  filters: { display: 'flex', gap: '20px', marginBottom: '20px', padding: '15px', background: '#f9f9f9', border: '1px solid #eee' },
  formGroup: { marginBottom: '15px' },
  label: { display: 'block', marginBottom: '5px', fontWeight: 'bold' },
  input: { width: '100%', padding: '8px', boxSizing: 'border-box' },
  select: { padding: '8px', fontSize: '1rem' },
  button: { padding: '10px 15px', cursor: 'pointer', backgroundColor: '#28a745', color: 'white', border: 'none' },
  message: { marginTop: '15px', padding: '10px', border: '1px solid' },
  success: { borderColor: 'green', color: 'green', background: '#e6ffed' },
  error: { borderColor: 'red', color: 'red', background: '#ffebee' }
};

// 개별 등록 폼 초기 상태
const initialFormData = {
  name: '',
  rrn: '',
  phone: '',
  email: '',
  bankName: '',
  bankAccount: '',
  businessName: '',
  workplace: '',
  district: '',
  address: '',
  programDetail: '',
  status: 'active', 
  startDate: '',
  endDate: '',
};

const ParticipantRegisterPage = () => {
  const [activeTab, setActiveTab] = useState('individual'); 
  
  // 공통 필터
  // ⬇️ [수정] 'selectedYear'의 기본값으로 현재 연도를 설정합니다.
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [projects, setProjects] = useState([]); 
  const [selectedProject, setSelectedProject] = useState(''); 

  // 상태 메시지
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // 개별 등록
  const [formData, setFormData] = useState(initialFormData);
  
  // 일괄 등록
  const [file, setFile] = useState(null);
  const [excelPassword, setExcelPassword] = useState('');
  // 일괄 수정
  const [fileBulkUpdate, setFileBulkUpdate] = useState(null);
  const [bulkUpdatePassword, setBulkUpdatePassword] = useState('');
  const [bulkUpdateResult, setBulkUpdateResult] = useState(null);
  // 지정 연도 데이터 가져오기
  const [sourceYear, setSourceYear] = useState(new Date().getFullYear() - 1);
  const [importEmail, setImportEmail] = useState(true);
  const [importAccount, setImportAccount] = useState(true);
  const [importAddress, setImportAddress] = useState(true);
  const [importResult, setImportResult] = useState(null);

  const bulkFileInputRef = useRef(null);
  const bulkUpdateFileInputRef = useRef(null);

  const clearBulkFileSelection = useCallback(() => {
    setFile(null);
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }
  }, []);

  const clearBulkUpdateFileSelection = useCallback(() => {
    setFileBulkUpdate(null);
    if (bulkUpdateFileInputRef.current) {
      bulkUpdateFileInputRef.current.value = '';
    }
  }, []);

  // 1. 연도 변경 시, 해당 연도의 사업 목록(Project) 불러오기
  const fetchProjects = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const res = await api.get(`/projects?year=${selectedYear}`);
      setProjects(res.data);
      setSelectedProject(''); 
      setLoading(false);
    } catch (err) {
      setError('사업 목록을 불러오는 데 실패했습니다.');
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // 2. 공통 핸들러 (연도, 사업 선택)
  const handleYearChange = (e) => {
    const y = parseInt(e.target.value, 10);
    setSelectedYear(y);
    setProjects([]);
    setSelectedProject('');
    setSourceYear((prev) => (prev === selectedYear - 1 ? y - 1 : prev));
  };
  
  // --- 개별 등록 ( ... 변경 없음 ... ) ---
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIndividualSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject) {
      setError('먼저 연도와 사업명을 선택하세요.');
      return;
    }
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const res = await api.post('/participants/individual', {
        ...formData,
        memo: formData.businessName,
        status: 'active',
        year: selectedYear,
        projectId: selectedProject,
      });
      setMessage(res.data.message);
      setFormData(initialFormData); // 폼 초기화
    } catch (err) {
      let errMsg = err.response?.data?.message || '참여자 등록에 실패했습니다.';
      if (err.response?.status === 409 && err.response?.data?.duplicateParticipant) {
        const dup = err.response.data.duplicateParticipant;
        errMsg += ` (기존 명단: ${dup.name}, 주민번호: ${dup.rrn})`;
      }
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // --- 일괄 등록 ( ... 변경 없음 ... ) ---
  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject || !file) {
      setError('연도, 사업명, 엑셀 파일을 모두 선택해야 합니다.');
      return;
    }
    setError('');
    setMessage('');
    setLoading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('year', selectedYear);
    uploadFormData.append('projectId', selectedProject);
    uploadFormData.append('excelFile', file); 
    if (excelPassword) {
      uploadFormData.append('password', excelPassword);
    }
    try {
      const res = await api.post('/participants/bulk', uploadFormData);
      let resultMsg = res.data.message || `일괄 등록 완료 (성공: ${res.data.successCount}, 실패: ${res.data.errorCount})`;
      if (res.data.skippedDuplicateCount > 0 && res.data.duplicateNames?.length) {
        resultMsg += `\n중복 제외 명단: ${res.data.duplicateNames.join(', ')}`;
      }
      setMessage(resultMsg);
      if (res.data.errors && res.data.errors.length > 0) {
        setError(`실패 사유:\n- ${res.data.errors.join('\n- ')}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || '일괄 업로드에 실패했습니다.');
    } finally {
      setLoading(false);
      setExcelPassword('');
      clearBulkFileSelection();
    }
  };

  // --- 일괄 수정 (Excel) ---
  const handleFileBulkUpdateChange = (e) => {
    setFileBulkUpdate(e.target.files?.[0] ?? null);
    setBulkUpdateResult(null);
  };

  const handleBulkUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject || !fileBulkUpdate) {
      setError('연도, 사업명, 엑셀 파일을 모두 선택해야 합니다.');
      return;
    }
    setError('');
    setMessage('');
    setBulkUpdateResult(null);
    setLoading(true);
    const formData = new FormData();
    formData.append('year', selectedYear);
    formData.append('projectId', selectedProject);
    formData.append('excelFile', fileBulkUpdate);
    if (bulkUpdatePassword) {
      formData.append('password', bulkUpdatePassword);
    }
    try {
      const res = await api.post('/participants/bulk-update', formData);
      setBulkUpdateResult(res.data);
      setMessage(res.data.message || '일괄 수정 처리 완료');
    } catch (err) {
      setError(err.response?.data?.message || '일괄 수정에 실패했습니다.');
    } finally {
      setLoading(false);
      setBulkUpdatePassword('');
      clearBulkUpdateFileSelection();
    }
  };

  // --- 지정 연도 데이터 가져오기 ---
  const handleImportFromYear = async (e) => {
    e.preventDefault();
    if (!selectedProject) {
      setError('연도와 사업을 선택하세요.');
      return;
    }
    if (!importEmail && !importAccount && !importAddress) {
      setError('가져올 항목(이메일, 계좌정보, 주소 중 하나 이상)을 선택하세요.');
      return;
    }
    setError('');
    setMessage('');
    setImportResult(null);
    setLoading(true);
    try {
      const res = await api.post('/participants/import-from-year', {
        year: selectedYear,
        projectId: selectedProject,
        sourceYear: sourceYear || selectedYear - 1,
        importEmail: !!importEmail,
        importAccount: !!importAccount,
        importAddress: !!importAddress,
      });
      setImportResult(res.data);
      setMessage(res.data.message || '가져오기 완료');
    } catch (err) {
      setError(err.response?.data?.message || '가져오기에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.pageContainer}>
      <h2>참여자 등록</h2>
      
      {/* 1. 탭 버튼 ( ... 변경 없음 ... ) */}
      <div style={styles.tabs}>
        <button
          style={{...styles.tabButton, ...(activeTab === 'individual' && styles.activeTab)}}
          onClick={() => setActiveTab('individual')}
        >
          개별 등록
        </button>
        <button
          style={{...styles.tabButton, ...(activeTab === 'bulk' && styles.activeTab)}}
          onClick={() => setActiveTab('bulk')}
        >
          일괄 등록 (Excel)
        </button>
        <button
          style={{...styles.tabButton, ...(activeTab === 'bulkUpdate' && styles.activeTab)}}
          onClick={() => setActiveTab('bulkUpdate')}
        >
          일괄수정 (Excel)
        </button>
        <button
          style={{...styles.tabButton, ...(activeTab === 'importYear' && styles.activeTab)}}
          onClick={() => setActiveTab('importYear')}
        >
          지정 연도 데이터 가져오기
        </button>
      </div>

      {/* 2. 탭 콘텐츠 */}
      <div style={styles.tabContent}>
        {/* 2-1. 공통 필터 (연도, 사업 선택) */}
        <div style={styles.filters}>
          <div>
            <label style={styles.label}>등록 연도</label>
            {/* ⬇️ [수정] <select>를 <input type="number">로 변경 */}
            <input
              type="number"
              style={styles.select}
              value={selectedYear}
              onChange={handleYearChange}
              placeholder="연도 입력 (예: 2024)"
              min="2020"
              step="1"
            />
          </div>
          <div>
            <label style={styles.label}>사업명</label>
            <select 
              style={styles.select} 
              value={selectedProject} 
              onChange={(e) => setSelectedProject(e.target.value)}
              disabled={loading || projects.length === 0}
            >
              <option value="">-- {loading ? "불러오는 중..." : "사업명 선택"} --</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.projectName}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 메시지 표시 ( ... 변경 없음 ... ) */}
        {message && <div style={{...styles.message, ...styles.success}}>{message}</div>}
        {error && <div style={{...styles.message, ...styles.error, whiteSpace: 'pre-wrap'}}>{error}</div>}

        {/* 2-2. 개별 등록 폼 ( ... 변경 없음 ... ) */}
        {activeTab === 'individual' && (
          <form onSubmit={handleIndividualSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>성명</label>
              <input type="text" name="name" style={styles.input} value={formData.name} onChange={handleFormChange} required />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>주민등록번호</label>
              <input type="text" name="rrn" style={styles.input} value={formData.rrn} onChange={handleFormChange} required />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>연락처</label>
              <input type="tel" name="phone" style={styles.input} value={formData.phone} onChange={handleFormChange} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>이메일 (선택)</label>
              <input
                type="email"
                name="email"
                style={styles.input}
                value={formData.email}
                onChange={handleFormChange}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>은행</label>
              <input type="text" name="bankName" style={styles.input} value={formData.bankName} onChange={handleFormChange} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>계좌번호</label>
              <input type="text" name="bankAccount" style={styles.input} value={formData.bankAccount} onChange={handleFormChange} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>메모 (선택)</label>
              <input type="text" name="businessName" style={styles.input} value={formData.businessName} onChange={handleFormChange} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>근무장소</label>
              <input type="text" name="workplace" style={styles.input} value={formData.workplace} onChange={handleFormChange} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>읍면동</label>
              <input type="text" name="district" style={styles.input} value={formData.district} onChange={handleFormChange} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>주소</label>
              <input type="text" name="address" style={styles.input} value={formData.address} onChange={handleFormChange} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>세부 프로그램</label>
              <input type="text" name="programDetail" style={styles.input} value={formData.programDetail} onChange={handleFormChange} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>참여시작일</label>
              <DateInput value={formData.startDate} onChange={(v) => setFormData(prev => ({ ...prev, startDate: v }))} inputStyle={styles.input} required />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>참여종료일 (선택)</label>
              <DateInput value={formData.endDate} onChange={(v) => setFormData(prev => ({ ...prev, endDate: v }))} inputStyle={styles.input} />
            </div>
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? '등록 중...' : '개별 등록하기'}
            </button>
          </form>
        )}

        {/* 2-3. 일괄 등록 폼 ( ... 변경 없음 ... ) */}
        {activeTab === 'bulk' && (
          <form onSubmit={handleBulkSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>엑셀 파일 업로드</label>
              <div style={{
                padding: '15px',
                marginBottom: '15px',
                backgroundColor: '#e3f2fd',
                border: '2px solid #2196f3',
                borderRadius: '5px',
                fontSize: '14px',
                lineHeight: '1.6',
                fontWeight: '500'
              }}>
                <div style={{ color: '#1976d2', fontWeight: 'bold', marginBottom: '8px', fontSize: '15px' }}>
                  💡 추천 방법
                </div>
                <div style={{ color: '#1565c0' }}>
                  노인일자리 정보시스템의 <strong>[참여자 → 사업별 참여자 관리]</strong>에서 
                  <strong> 참여중인 명단만 출력(주민번호 전체 추출)</strong>한 엑셀 파일에 
                  <strong style={{ color: '#d32f2f' }}> 이메일 항목만 추가</strong>하여 입력하시는 것을 권장합니다.
                </div>
                <div style={{ color: '#1565c0', marginTop: '10px' }}>
                  ※ 동일 연도·성명·주민번호로 이미 등록된 인원은 제외되고, <strong>신규 인원만</strong> 등록됩니다. 기존 인원 데이터는 변경되지 않습니다.
                </div>
              </div>
              <p>※ 템플릿: [성명], [주민등록번호], [연락처], [이메일], [은행], [계좌번호], [메모], [근무장소], [읍면동], [주소], [세부프로그램] 또는 [세부 프로그램], [참여시작일], [참여종료일] 컬럼 순서 (참여상태는 자동으로 '참여중'으로 저장)</p>
              <input
                id="file-input"
                ref={bulkFileInputRef}
                type="file"
                style={styles.input}
                onChange={handleFileChange}
                accept=".xlsx, .xls"
              />
              <div style={{ marginTop: '12px' }}>
                <label style={styles.label}>엑셀 파일 비밀번호 (있는 경우에만)</label>
                <input
                  type="password"
                  style={styles.input}
                  value={excelPassword}
                  onChange={(e) => setExcelPassword(e.target.value)}
                  placeholder="비밀번호 없는 엑셀은 비워두세요"
                />
              </div>
            </div>
            <button type="submit" style={styles.button} disabled={loading || !file}>
              {loading ? '업로드 중...' : '일괄 등록하기'}
            </button>
          </form>
        )}

        {/* 2-4. 일괄 수정 (Excel) */}
        {activeTab === 'bulkUpdate' && (
          <form onSubmit={handleBulkUpdateSubmit}>
            <div style={{
              padding: '16px',
              marginBottom: '20px',
              backgroundColor: '#fff8e1',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              fontSize: '14px',
              lineHeight: '1.7',
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#f57c00' }}>
                📌 안내
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li><strong>기존에 등록된 명단에 한해 수정만 가능</strong>합니다. 성명·주민등록번호가 일치하는 참여자만 엑셀 내용으로 갱신됩니다.</li>
                <li><strong>신규 명단은 여기서 입력할 수 없습니다.</strong> 새 참여자는 <strong>일괄 등록 (Excel)</strong> 또는 <strong>개별 등록</strong>에서 등록해 주세요.</li>
                <li>엑셀에 <strong>값이 있는 항목만</strong> 반영되고, <strong>빈 칸은 기존값이 유지</strong>됩니다.</li>
                <li>엑셀에 없는 참여자(기존 명단에만 있는 사람)는 <strong>변경되지 않습니다.</strong></li>
                <li>연도·사업을 먼저 선택한 뒤, 일괄 등록과 동일한 컬럼 구조의 엑셀을 업로드해 주세요.</li>
              </ul>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>엑셀 파일 업로드</label>
              <p style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>
                ※ [성명], [주민등록번호], [연락처], [이메일], [은행], [계좌번호], [메모], [근무장소], [읍면동], [주소], [세부프로그램] 또는 [세부 프로그램], [참여시작일], [참여종료일] 컬럼 (성명·주민번호로 기존 명단과 매칭)
              </p>
              <input
                id="file-bulk-update-input"
                ref={bulkUpdateFileInputRef}
                type="file"
                style={styles.input}
                onChange={handleFileBulkUpdateChange}
                accept=".xlsx, .xls"
              />
              <div style={{ marginTop: '12px' }}>
                <label style={styles.label}>엑셀 파일 비밀번호 (있는 경우에만)</label>
                <input
                  type="password"
                  style={styles.input}
                  value={bulkUpdatePassword}
                  onChange={(e) => setBulkUpdatePassword(e.target.value)}
                  placeholder="비밀번호 없는 엑셀은 비워두세요"
                />
              </div>
            </div>
            <button type="submit" style={styles.button} disabled={loading || !fileBulkUpdate}>
              {loading ? '처리 중...' : '일괄 수정하기'}
            </button>

            {bulkUpdateResult && (
              <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#e8f5e9',
                border: '1px solid #4caf50',
                borderRadius: '8px',
                fontSize: '14px',
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '12px', color: '#2e7d32' }}>처리 결과</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span><strong>{bulkUpdateResult.updatedCount ?? 0}명</strong> 수정됨</span>
                  <span><strong>{bulkUpdateResult.skippedNewCount ?? 0}명</strong> 신규로 입력 불가 (엑셀에 있으나 기존 명단에 없음 → 일괄 등록 또는 개별 등록 이용)</span>
                  <span><strong>{bulkUpdateResult.unchangedCount ?? 0}명</strong> 기존 데이터와 동일 (변경 없음)</span>
                </div>
                {bulkUpdateResult.errors && bulkUpdateResult.errors.length > 0 && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #81c784' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#c62828' }}>참고·오류</div>
                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#b71c1c' }}>
                      {bulkUpdateResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </form>
        )}

        {/* 2-5. 지정 연도 데이터 가져오기 */}
        {activeTab === 'importYear' && (
          <form onSubmit={handleImportFromYear}>
            <div style={{
              padding: '16px',
              marginBottom: '20px',
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '8px',
              fontSize: '14px',
              lineHeight: '1.7',
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#1565c0' }}>
                📌 안내
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li><strong>당해 연도·사업</strong>의 참여자 명단을 기준으로, <strong>가져올 연도</strong>에서 이름·주민등록번호가 같은 사람의 데이터를 찾습니다.</li>
                <li>가져올 연도는 <strong>전체 명단</strong>에서 조회합니다 (사업이 바뀌어도 동일 인물이면 가져옵니다).</li>
                <li><strong>당해 명단에서 비어 있는 항목만</strong> 채웁니다. 이미 값이 있는 칸은 변경하지 않습니다.</li>
                <li>가져올 항목은 <strong>이메일</strong>, <strong>계좌정보(은행, 계좌번호)</strong>, <strong>주소</strong> 중 필요한 것만 체크해서 선택하세요.</li>
              </ul>
            </div>
            <div style={{ ...styles.formGroup, display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-end' }}>
              <div>
                <label style={styles.label}>가져올 연도</label>
                <input
                  type="number"
                  style={styles.select}
                  value={sourceYear}
                  onChange={(e) => setSourceYear(parseInt(e.target.value, 10) || '')}
                  min="2020"
                  max={selectedYear - 1}
                  placeholder="예: 전년도"
                />
              </div>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={importEmail} onChange={(e) => setImportEmail(e.target.checked)} />
                  이메일
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={importAccount} onChange={(e) => setImportAccount(e.target.checked)} />
                  계좌정보 (은행, 계좌번호)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={importAddress} onChange={(e) => setImportAddress(e.target.checked)} />
                  주소
                </label>
              </div>
            </div>
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? '처리 중...' : '일괄 가져오기'}
            </button>

            {importResult && (
              <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#e8f5e9',
                border: '1px solid #4caf50',
                borderRadius: '8px',
                fontSize: '14px',
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '12px', color: '#2e7d32' }}>처리 결과</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span><strong>{importResult.emailFilledCount ?? 0}명</strong> 이메일 반영 (빈 칸만 채움)</span>
                  <span><strong>{importResult.accountFilledCount ?? 0}명</strong> 계좌정보 반영 (빈 칸만 채움)</span>
                  <span><strong>{importResult.addressFilledCount ?? 0}명</strong> 주소 반영 (빈 칸만 채움)</span>
                  <span><strong>{importResult.skippedNoMatch ?? 0}명</strong> 가져올 연도 명단에 없음 (매칭 제외)</span>
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #81c784' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#c62828' }}>참고·오류</div>
                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#b71c1c' }}>
                      {importResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ParticipantRegisterPage;